import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, GiftIcon, PackageIcon } from 'lucide-react';
import { getRouteApi, Link, type RouteIds, useRouter } from '@tanstack/react-router';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { Button } from '@ui/button.tsx';
import { useApiOrdersIdGet } from '@r/api/generated/client.ts';
import { OrderRecipientGrid } from './order-recipient-grid.tsx';
import { OrderProductsTableCard } from './order-products-table-card.tsx';
import { OrderDetailContentSkeleton } from './order-detail-content-skeleton.tsx';

type OrderProps = { admin?: boolean };

export const Order = ({ admin = false }: OrderProps) => {
  const { t } = useTranslation();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const router = useRouter();

  const routeApi = admin
    ? getRouteApi('/auth-layout/shops/orders/$id' as RouteIds<typeof router.routeTree>)
    : getRouteApi('/orders/$id' as RouteIds<typeof router.routeTree>);

  const { id: orderId } = routeApi.useParams() as { id: string };

  const {
    data: order,
    isSuccess: isOrderSuccess,
    isPending: isOrderPending,
    isError: isOrderError
  } = useApiOrdersIdGet(orderId, {
    query: {
      queryKey: ['order_id', workspaces.workspace?.id, user?.id, orderId],
      enabled: !!workspaces.workspace?.id
    }
  });

  const backButton = (
    <Link
      to=".."
      className="inline-flex gap-2 items-center text-sm font-medium text-primary hover:underline"
    >
      <Button rounded="full">
        <ArrowLeftIcon />
        {t('common.back')}
      </Button>
    </Link>
  );

  const handleProductRowClick = (productId: number) => {
    if (!admin || !order) return;
    window.location.assign(`/shops/orders/${order.id}/reward-obtained/${productId}`);
  };

  const OrderDetailContent = () => (
    <div className="space-y-5">
      <h1 className="flex gap-2 items-center text-xl font-bold">
        <GiftIcon />
        <span className="text-xl font-bold">{t('admin.order.detail.table.title')}</span>
      </h1>

      <div className="flex flex-col gap-2.5">
        {(isOrderPending || isOrderError) && <OrderDetailContentSkeleton />}

        {isOrderSuccess && order && (
          <div className="space-y-6">
            <OrderProductsTableCard
              order={order}
              admin={admin}
              onProductRowClick={admin ? handleProductRowClick : undefined}
            />

            <OrderRecipientGrid order={order} admin={admin} />
          </div>
        )}
      </div>
    </div>
  );

  return admin ? (
    <div className="space-y-10">
      <div>{backButton}</div>
      <OrderDetailContent />
    </div>
  ) : (
    <div className="overflow-x-hidden flex-grow">
      <div className="container flex flex-col space-y-10 h-full text-foreground">
        <span>
          <div className="flex flex-nowrap items-center mb-3">
            <PackageIcon className="mr-3" />
            <h1 className="flex items-center font-bold text-2xl md:text-3xl lg:text-[2.75rem] lg:leading-[3.5rem]">
              {t('menu.account.order.tracking')}
            </h1>
          </div>
          <p className="font-light text-muted-foreground">{t('order.tracking.description')}</p>
          <div className="mt-6">{backButton}</div>
        </span>
        <OrderDetailContent />
      </div>
    </div>
  );
};
